import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { useIsQaScannerEnabled } from "@components/QaReview/hooks/useIsQaScannerEnabled";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";

interface UseFirstTimeDisclaimerArgs {
  isReportCompleted: boolean;
  timeoutMs?: number;
}

// PROA-3297: Confirm is the only way past the notice, so it is remembered in this browser per signed-in user.
const CONFIRMED_KEY_PREFIX = "qa-disclaimer-confirmed";

export const disclaimerConfirmedKey = (userId: string) => `${CONFIRMED_KEY_PREFIX}:${userId}`;

const subscribeToStorage = (onChange: () => void) => {
  window.addEventListener("storage", onChange);
  return () => window.removeEventListener("storage", onChange);
};

// A browser that blocks site storage throws on any access. Review and Sign must still load there, so the notice
// falls back to this page load only (shown until Confirm, back on reload) and the error goes to RUM.
const readConfirmed = (storageKey: string, onError: (error: unknown) => void): boolean => {
  try {
    return localStorage.getItem(storageKey) !== null;
  } catch (error) {
    onError(error);
    return false;
  }
};

const writeConfirmed = (storageKey: string, onError: (error: unknown) => void) => {
  try {
    localStorage.setItem(storageKey, new Date().toISOString());
  } catch (error) {
    onError(error);
  }
};

export const useFirstTimeDisclaimer = ({ isReportCompleted, timeoutMs = 7000 }: UseFirstTimeDisclaimerArgs) => {
  const isQaScannerEnabled = useIsQaScannerEnabled();
  const { generationStatus: suggestionGenerationStatus } = useReviewData();
  const { user } = useAuth0();
  const storageKey = user?.sub ? disclaimerConfirmedKey(user.sub) : null;
  // The snapshot runs on every render, so a blocked-storage error is reported once per mount
  const isStorageErrorReportedRef = useRef(false);
  const reportStorageError = (error: unknown) => {
    if (isStorageErrorReportedRef.current) return;
    isStorageErrorReportedRef.current = true;
    datadogRum.addError(error, { source: "qa-disclaimer-storage" });
  };
  // Only Quality Review pages read storage
  const isConfirmedInStorage = useSyncExternalStore(
    subscribeToStorage,
    () => isQaScannerEnabled && storageKey !== null && readConfirmed(storageKey, reportStorageError),
  );
  const [isConfirmedThisPage, setIsConfirmedThisPage] = useState(false);

  const isFirstTimeVisitRef = useRef<boolean | null>(null);
  const [timeoutReached, setTimeoutReached] = useState(false);

  // Capture first-time visit ONCE
  if (isFirstTimeVisitRef.current === null) {
    isFirstTimeVisitRef.current = suggestionGenerationStatus === null;
  }
  const isFirstTimeVisit = isFirstTimeVisitRef.current === true;

  // Caps how long the first scan's progress screen shows
  useEffect(() => {
    if (!isQaScannerEnabled || isReportCompleted || !isFirstTimeVisit) {
      return;
    }
    const timer = setTimeout(() => setTimeoutReached(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [isQaScannerEnabled, isReportCompleted, isFirstTimeVisit, timeoutMs]);

  const confirmDisclaimer = () => {
    setIsConfirmedThisPage(true);
    if (storageKey) {
      writeConfirmed(storageKey, reportStorageError);
    }
  };

  return {
    showDisclaimer: isQaScannerEnabled && !isReportCompleted && !isConfirmedThisPage && !isConfirmedInStorage,
    confirmDisclaimer,
    timeoutReached,
  };
};
